// Considered: LoginPage (Center-PWA) — rejected because this view is public and
// unauthenticated with no Descope session, so it must render outside `App` and cannot
// share LoginPage's flow-widget shell. See spec Reuse Audit.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import './WaitlistStatusPageOrganism.scss';

export type WaitlistStatusResult =
  | { status: 'pending' | 'approved' | 'rejected' }
  | { status: 'not_found' };

type WaitlistStatusView =
  | 'form'
  | 'loading'
  | 'error'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'not_found';

export interface WaitlistStatusPageOrganismProps {
  /** Prefills the email field and checks status immediately — the PWA passes `?email=` here. */
  initialEmail?: string;
  /**
   * Looks up waitlist status for an email. Resolve `{ status: 'not_found' }` for a 404 —
   * only a network/5xx failure should reject, since that's what drives the error state.
   */
  checkStatus: (email: string) => Promise<WaitlistStatusResult>;
  /** Sign-in and sign-up land on the same Descope flow — one callback covers both. */
  onNavigateToSignIn: () => void;
}

const WaitlistStatusPageOrganism: React.FC<WaitlistStatusPageOrganismProps> = ({
  initialEmail,
  checkStatus,
  onNavigateToSignIn,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [lastCheckedEmail, setLastCheckedEmail] = useState(initialEmail ?? '');
  const [view, setView] = useState<WaitlistStatusView>(initialEmail ? 'loading' : 'form');

  const runCheck = useCallback(
    async (targetEmail: string) => {
      setLastCheckedEmail(targetEmail);
      setView('loading');
      try {
        const result = await checkStatus(targetEmail);
        setView(result.status);
      } catch {
        setView('error');
      }
    },
    [checkStatus],
  );

  useEffect(() => {
    if (initialEmail) {
      runCheck(initialEmail);
    }
    // Only re-run when the prefilled email itself changes, not on every `runCheck` identity
    // change (a new `checkStatus` prop on re-render must not re-trigger the initial lookup).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = email.trim();
      if (trimmed) {
        runCheck(trimmed);
      }
    },
    [email, runCheck],
  );

  const handleRetry = useCallback(() => {
    runCheck(lastCheckedEmail);
  }, [lastCheckedEmail, runCheck]);

  return (
    <main className="waitlist-status-page">
      <div className="waitlist-status-page__panel">
        {view === 'form' && (
          <form
            className="waitlist-status-page__form"
            onSubmit={handleSubmit}
            data-testid="waitlist-status-page-form"
          >
            <h1 className="waitlist-status-page__title">{t('waitlist.status.form.title')}</h1>
            <p className="waitlist-status-page__description">
              {t('waitlist.status.form.description')}
            </p>
            <label htmlFor="waitlist-status-page-email">
              {t('waitlist.status.form.emailLabel')}
            </label>
            <InputAtom
              id="waitlist-status-page-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t('waitlist.status.form.emailPlaceholder')}
            />
            <ButtonAtom
              type="submit"
              label={t('waitlist.status.form.submit')}
              variant="primary"
              disabled={!email.trim()}
            />
          </form>
        )}

        {view === 'loading' && (
          <div className="waitlist-status-page__loading" data-testid="waitlist-status-page-loading">
            <SkeletonAtom count={2} height="1.5rem" />
          </div>
        )}

        {view === 'error' && (
          <div className="waitlist-status-page__error" role="alert" data-testid="waitlist-status-page-error">
            <h1 className="waitlist-status-page__title">{t('waitlist.status.error.title')}</h1>
            <p className="waitlist-status-page__description">{t('waitlist.status.error.message')}</p>
            <ButtonAtom
              label={t('waitlist.status.error.retry')}
              variant="primary"
              onClick={handleRetry}
            />
          </div>
        )}

        {view === 'pending' && (
          <div className="waitlist-status-page__result" data-testid="waitlist-status-page-pending">
            <StatusBadgeAtom status="pending" label={t('waitlist.status.pending.badge')} showIcon />
            <h1 className="waitlist-status-page__title">{t('waitlist.status.pending.title')}</h1>
            <p className="waitlist-status-page__description">{t('waitlist.status.pending.message')}</p>
          </div>
        )}

        {view === 'approved' && (
          <div className="waitlist-status-page__result" data-testid="waitlist-status-page-approved">
            <StatusBadgeAtom status="active" label={t('waitlist.status.approved.badge')} showIcon />
            <h1 className="waitlist-status-page__title">{t('waitlist.status.approved.title')}</h1>
            <p className="waitlist-status-page__description">{t('waitlist.status.approved.message')}</p>
            <ButtonAtom
              label={t('waitlist.status.approved.goToSignIn')}
              variant="primary"
              onClick={onNavigateToSignIn}
            />
          </div>
        )}

        {view === 'rejected' && (
          <div className="waitlist-status-page__result" data-testid="waitlist-status-page-rejected">
            <StatusBadgeAtom status="error" label={t('waitlist.status.rejected.badge')} showIcon />
            <h1 className="waitlist-status-page__title">{t('waitlist.status.rejected.title')}</h1>
            <p className="waitlist-status-page__description">{t('waitlist.status.rejected.message')}</p>
            <p className="waitlist-status-page__support">{t('waitlist.status.rejected.contactSupport')}</p>
          </div>
        )}

        {view === 'not_found' && (
          <EmptyStateAtom
            icon="pi-search"
            title={t('waitlist.status.notFound.title')}
            description={t('waitlist.status.notFound.message')}
            data-testid="waitlist-status-page-not-found"
          >
            <ButtonAtom
              label={t('waitlist.status.notFound.signUp')}
              variant="primary"
              onClick={onNavigateToSignIn}
            />
          </EmptyStateAtom>
        )}
      </div>
    </main>
  );
};

export default WaitlistStatusPageOrganism;
